import type { ExperienceScenario } from "../../experience";
import type { HomeSurface } from "./resolveHomeExperience";

export type HomeLayoutPattern =
  | "guided-plan"
  | "scenario-concept"
  | "cycle-dashboard"
  | "waiting-dashboard"
  | "helper-journey";

export type HomeStepperSemantics = "planning-only" | "not-a-stepper";

export type HomeDataDependency =
  | "profile"
  | "family-building-plan"
  | "manual-step-completion"
  | "medical-history"
  | "location"
  | "coverage"
  | "baseline-tests"
  | "clinic-shortlist"
  | "cycle-start"
  | "active-cycle"
  | "cycle-phases"
  | "schedule"
  | "medications"
  | "monitoring"
  | "semen-analysis"
  | "embryology"
  | "cryo-inventory"
  | "money"
  | "cycle-outcome"
  | "helper-application"
  | "partner-context";

export type HomeActionCapability =
  | "edit-plan"
  | "complete-step"
  | "open-medical-history"
  | "open-tests"
  | "open-insurance"
  | "find-care"
  | "open-programs"
  | "start-cycle"
  | "open-cycle"
  | "open-schedule"
  | "open-outcome-funnel"
  | "open-cryo-vault"
  | "log-outcome"
  | "helper-apply"
  | "partner-task";

export type HomeSectionContract = {
  id: string;
  label: string;
  purpose: string;
  dataDependencies: HomeDataDependency[];
  actions: HomeActionCapability[];
  reusableAcross: ExperienceScenario[];
};

export type HomeSurfaceContract = {
  surface: HomeSurface;
  layoutPattern: HomeLayoutPattern;
  stepperSemantics: HomeStepperSemantics;
  purpose: string;
  dataDependencies: HomeDataDependency[];
  sections: HomeSectionContract[];
  notes: string[];
};

export const EGG_FREEZING_PRE_CYCLE_SECTION_IDS = {
  understand: "understand-egg-freezing",
  knowYourNumbers: "know-your-numbers",
  findCare: "find-care",
  startCycle: "start-cycle",
} as const;

export const EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS = {
  understand: "understand-embryo-freezing",
  knowYourNumbers: "know-both-sides",
  optimizeInputs: "optimize-inputs",
  findCare: "find-embryo-care",
  startCycle: "start-embryo-cycle",
} as const;

export const SPERM_FREEZING_PRE_CYCLE_SECTION_IDS = {
  understand: "understand-sperm-freezing",
  knowYourBaseline: "know-sperm-baseline",
  improveInputs: "improve-sperm-inputs",
  findCare: "find-sperm-care",
  banking: "start-sperm-banking",
} as const;

const PLANNING_REUSE_SCENARIOS: ExperienceScenario[] = [
  "planning_ovarian_preservation",
  "planning_embryo_creation",
  "planning_sperm_preservation",
  "planning_ivf",
  "planning_iui",
];

export const EGG_FREEZING_PRE_CYCLE_SECTIONS: HomeSectionContract[] = [
  {
    id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.understand,
    label: "Understand",
    purpose:
      "Explain the relevant biology and the treatment shape before a clinic or protocol exists.",
    dataDependencies: [
      "profile",
      "family-building-plan",
      "manual-step-completion",
    ],
    actions: ["complete-step", "open-tests"],
    reusableAcross: [
      "planning_ovarian_preservation",
      "planning_embryo_creation",
    ],
  },
  {
    id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.knowYourNumbers,
    label: "Know your numbers",
    purpose:
      "Collect medical history, baseline tests, coverage, and estimate inputs so the plan can become personalized.",
    dataDependencies: [
      "profile",
      "medical-history",
      "coverage",
      "baseline-tests",
      "manual-step-completion",
    ],
    actions: [
      "complete-step",
      "open-medical-history",
      "open-tests",
      "open-insurance",
    ],
    reusableAcross: PLANNING_REUSE_SCENARIOS,
  },
  {
    id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.findCare,
    label: "Find care",
    purpose:
      "Use location, coverage, goals, and care preferences to route toward clinics or matching support.",
    dataDependencies: [
      "location",
      "coverage",
      "medical-history",
      "clinic-shortlist",
      "manual-step-completion",
    ],
    actions: ["find-care", "open-programs", "open-insurance", "complete-step"],
    reusableAcross: PLANNING_REUSE_SCENARIOS,
  },
  {
    id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.startCycle,
    label: "Start treatment",
    purpose:
      "Transition from planning into active cycle tracking once a clinic and cycle start exist.",
    dataDependencies: ["cycle-start", "active-cycle"],
    actions: ["start-cycle"],
    reusableAcross: [
      "planning_ovarian_preservation",
      "planning_embryo_creation",
      "planning_sperm_preservation",
      "planning_ivf",
      "planning_iui",
    ],
  },
];

export const EMBRYO_FREEZING_PRE_CYCLE_SECTIONS: HomeSectionContract[] = [
  {
    id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.understand,
    label: "Understand",
    purpose:
      "Explain how embryo freezing combines ovarian stimulation, sperm, fertilization, embryo culture, optional PGT-A, and freezing.",
    dataDependencies: [
      "profile",
      "family-building-plan",
      "manual-step-completion",
    ],
    actions: ["complete-step", "open-tests"],
    reusableAcross: ["planning_embryo_creation", "planning_ivf"],
  },
  {
    id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.knowYourNumbers,
    label: "Know your numbers",
    purpose:
      "Collect ovarian reserve data, semen analysis, relevant genetic screening, and coverage inputs before clinic selection.",
    dataDependencies: [
      "profile",
      "medical-history",
      "coverage",
      "baseline-tests",
      "manual-step-completion",
      "partner-context",
    ],
    actions: [
      "complete-step",
      "open-medical-history",
      "open-tests",
      "open-insurance",
    ],
    reusableAcross: ["planning_embryo_creation", "planning_ivf"],
  },
  {
    id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.optimizeInputs,
    label: "Improve inputs",
    purpose:
      "Help both partners understand the optimization window for egg quality, sperm quality, avoidable exposures, and evidence-based interventions.",
    dataDependencies: [
      "profile",
      "medical-history",
      "baseline-tests",
      "partner-context",
      "manual-step-completion",
    ],
    actions: ["complete-step", "open-programs", "open-tests"],
    reusableAcross: ["planning_embryo_creation", "planning_ivf"],
  },
  {
    id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.findCare,
    label: "Find care",
    purpose:
      "Route toward clinics and labs that fit embryo creation needs, including lab quality, fertilization method, PGT-A preferences, cost, and coverage.",
    dataDependencies: [
      "location",
      "coverage",
      "medical-history",
      "clinic-shortlist",
      "partner-context",
      "manual-step-completion",
    ],
    actions: ["find-care", "open-programs", "open-insurance", "complete-step"],
    reusableAcross: ["planning_embryo_creation", "planning_ivf"],
  },
  {
    id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.startCycle,
    label: "Start treatment",
    purpose:
      "Transition from planning into active tracking for stimulation, retrieval, sperm collection, fertilization, embryo culture, and freezing.",
    dataDependencies: ["cycle-start", "active-cycle"],
    actions: ["start-cycle"],
    reusableAcross: ["planning_embryo_creation", "planning_ivf"],
  },
];

export const SPERM_FREEZING_PRE_CYCLE_SECTIONS: HomeSectionContract[] = [
  {
    id: SPERM_FREEZING_PRE_CYCLE_SECTION_IDS.understand,
    label: "Understand",
    purpose:
      "Explain sperm production, semen analysis, freezing, vials, storage, and future use before a clinic or lab is selected.",
    dataDependencies: [
      "profile",
      "family-building-plan",
      "manual-step-completion",
    ],
    actions: ["complete-step", "open-tests"],
    reusableAcross: ["planning_sperm_preservation"],
  },
  {
    id: SPERM_FREEZING_PRE_CYCLE_SECTION_IDS.knowYourBaseline,
    label: "Know your baseline",
    purpose:
      "Collect medical history, semen analysis, coverage, and test context so sperm banking and optimization guidance can become personalized.",
    dataDependencies: [
      "profile",
      "medical-history",
      "coverage",
      "baseline-tests",
      "semen-analysis",
      "manual-step-completion",
    ],
    actions: [
      "complete-step",
      "open-medical-history",
      "open-tests",
      "open-insurance",
    ],
    reusableAcross: ["planning_sperm_preservation"],
  },
  {
    id: SPERM_FREEZING_PRE_CYCLE_SECTION_IDS.improveInputs,
    label: "Improve sperm inputs",
    purpose:
      "Surface the 72- to 90-day sperm reset window, avoidable exposures, baseline/retest plan, and when banking now is more appropriate than waiting.",
    dataDependencies: [
      "profile",
      "medical-history",
      "baseline-tests",
      "semen-analysis",
      "manual-step-completion",
    ],
    actions: ["complete-step", "open-programs", "open-tests"],
    reusableAcross: ["planning_sperm_preservation"],
  },
  {
    id: SPERM_FREEZING_PRE_CYCLE_SECTION_IDS.findCare,
    label: "Find care",
    purpose:
      "Route toward sperm banking labs, urology/andrology support, coverage checks, and matching help when relevant.",
    dataDependencies: [
      "location",
      "coverage",
      "medical-history",
      "clinic-shortlist",
      "manual-step-completion",
    ],
    actions: ["find-care", "open-programs", "open-insurance", "complete-step"],
    reusableAcross: ["planning_sperm_preservation"],
  },
  {
    id: SPERM_FREEZING_PRE_CYCLE_SECTION_IDS.banking,
    label: "Start banking",
    purpose:
      "Transition from planning into active sperm collection, sample reports, vials, and storage tracking.",
    dataDependencies: ["cycle-start", "active-cycle", "cryo-inventory"],
    actions: ["start-cycle"],
    reusableAcross: ["planning_sperm_preservation"],
  },
];

export const HOME_SURFACE_CONTRACTS: Record<HomeSurface, HomeSurfaceContract> =
  {
    "precycle-egg-freezing": {
      surface: "precycle-egg-freezing",
      layoutPattern: "guided-plan",
      stepperSemantics: "planning-only",
      purpose:
        "Guide an exploring or before-treatment user through education, data readiness, care selection, and cycle handoff.",
      dataDependencies: [
        "profile",
        "family-building-plan",
        "manual-step-completion",
        "medical-history",
        "location",
        "coverage",
        "baseline-tests",
        "clinic-shortlist",
        "cycle-start",
      ],
      sections: EGG_FREEZING_PRE_CYCLE_SECTIONS,
      notes: [
        "This is the reference implementation for guided planning surfaces.",
        "The stepper belongs here because the user is still learning and making setup decisions.",
        "Do not reuse this layout as-is for active cycles, waits, or post-cycle states.",
        "A connected partner reads this same surface in partner wording, without the actions only the patient can take.",
      ],
    },
    "precycle-embryo-freezing": {
      surface: "precycle-embryo-freezing",
      layoutPattern: "guided-plan",
      stepperSemantics: "planning-only",
      purpose:
        "Guide a before-cycle embryo freezing user through education, both-sided readiness data, optimization, care/lab fit, and cycle handoff.",
      dataDependencies: [
        "profile",
        "family-building-plan",
        "manual-step-completion",
        "medical-history",
        "location",
        "coverage",
        "baseline-tests",
        "clinic-shortlist",
        "partner-context",
        "cycle-start",
      ],
      sections: EMBRYO_FREEZING_PRE_CYCLE_SECTIONS,
      notes: [
        "This is a guided planning surface, not the active embryo lab dashboard.",
        "It should reuse egg-freezing ovarian education where relevant, but add semen, fertilization, embryo culture, PGT-A, and lab-fit concepts.",
        "The active-cycle embryo freezing home should use cycle-dashboard semantics once a cycle starts.",
      ],
    },
    "precycle-sperm-freezing": {
      surface: "precycle-sperm-freezing",
      layoutPattern: "guided-plan",
      stepperSemantics: "planning-only",
      purpose:
        "Guide a before-cycle sperm freezing user through sperm biology, baseline semen testing, optimization, lab selection, banking, and storage setup.",
      dataDependencies: [
        "profile",
        "family-building-plan",
        "manual-step-completion",
        "medical-history",
        "location",
        "coverage",
        "baseline-tests",
        "semen-analysis",
        "clinic-shortlist",
        "cycle-start",
        "cryo-inventory",
      ],
      sections: SPERM_FREEZING_PRE_CYCLE_SECTIONS,
      notes: [
        "This is a guided planning surface for sperm banking, not the active semen-results wait dashboard.",
        "The reset-program content should be data-backed by semen analysis and rerun results when available.",
        "Once collection or storage is active, the homepage should move to cycle-dashboard semantics.",
      ],
    },
    "active-treatment": {
      surface: "active-treatment",
      layoutPattern: "cycle-dashboard",
      stepperSemantics: "not-a-stepper",
      purpose:
        "Orient the user around what is happening today in the active cycle, what changed clinically, and what they need to do next.",
      dataDependencies: [
        "active-cycle",
        "cycle-phases",
        "schedule",
        "medications",
        "monitoring",
        "embryology",
        "money",
      ],
      sections: [],
      notes: [
        "Active care should be organized around cycle state, daily tasks, monitoring, lab updates, and waiting support.",
        "This surface should not render the guided planning stepper.",
      ],
    },
    helper: {
      surface: "helper",
      layoutPattern: "helper-journey",
      stepperSemantics: "planning-only",
      purpose:
        "Guide donors and carriers through application, screening, matching, legal, and cycle readiness.",
      dataDependencies: ["helper-application", "medical-history", "schedule"],
      sections: [],
      notes: [
        "Donation/carrier planning can be step-like, but it needs its own helper-specific content and data.",
      ],
    },
    "concept-home": {
      surface: "concept-home",
      layoutPattern: "scenario-concept",
      stepperSemantics: "not-a-stepper",
      purpose:
        "Render redesigned scenario-specific homepage concepts for primary planning, wait, result, donation, and intended-parent states that do not use a bespoke guided-plan surface.",
      dataDependencies: ["profile", "family-building-plan"],
      sections: [],
      notes: [
        "This surface is intentionally not an old stepper fallback; add a scenario-specific concept variant before routing new states here.",
        "A connected partner reads the same concept variant the shared journey gives the patient, in partner wording.",
      ],
    },
  };

export function getHomeSurfaceContract(
  surface: HomeSurface,
): HomeSurfaceContract {
  return HOME_SURFACE_CONTRACTS[surface];
}

export function usesGuidedPlan(surface: HomeSurface): boolean {
  return getHomeSurfaceContract(surface).layoutPattern === "guided-plan";
}
